import { Form, Input, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthFooterLink, AuthSubmitButton } from '@/components/auth';
import styles from '@/components/auth/AuthForm.module.scss';
import { ROUTES } from '@/constants/routes';
import { useMutationReset } from '@/hooks/useMutationReset';
import { useNotify } from '@/hooks/useNotify';
import {
  useForgotPasswordMutation,
  useVerifyOtpMutation,
} from '@/modules/auth/authApi';
import type { VerifyOtpPayload } from '@/types/auth';
import { resolveMutationResult } from '@/utils/apiResponse';
import { setPasswordResetVerified } from '@/utils/passwordResetSession';

const RESEND_COOLDOWN_SEC = 120;

function formatCooldown(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
}

type VerifyOtpLocationState = { apiMessage?: string | null };

export default function VerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();
  const notify = useNotify();
  const [searchParams] = useSearchParams();
  const emailFromQuery =
    searchParams.get('email') ?? searchParams.get('username') ?? '';

  const [verifyOtp, { isLoading: isVerifying, reset: resetVerify }] = useVerifyOtpMutation();
  const [resendOtp, { isLoading: isResending, reset: resetResend }] = useForgotPasswordMutation();
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SEC);

  const { control, handleSubmit, setValue } = useForm<VerifyOtpPayload>({
    defaultValues: { username: emailFromQuery, otp: '' },
  });

  useMutationReset(resetVerify);
  useMutationReset(resetResend);

  useEffect(() => {
    const apiMessage = (location.state as VerifyOtpLocationState | null)?.apiMessage;
    if (apiMessage) notify.success(apiMessage);
  }, [location.state, notify]);

  useEffect(() => {
    if (!emailFromQuery) {
      navigate(ROUTES.FORGOT_PASSWORD, { replace: true });
      return;
    }
    setValue('username', emailFromQuery);
  }, [emailFromQuery, navigate, setValue]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setInterval(() => {
      setCooldown((value) => (value > 0 ? value - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [cooldown]);

  const onSubmit = async (values: VerifyOtpPayload) => {
    const result = await resolveMutationResult(verifyOtp(values));

    if (!result.ok) {
      notify.error(result.message);
      return;
    }

    setPasswordResetVerified(values.username);
    navigate(`${ROUTES.RESET_PASSWORD}?email=${encodeURIComponent(values.username)}`, {
      replace: true,
      state: { apiMessage: result.message },
    });
  };

  const handleResend = async () => {
    if (!emailFromQuery || cooldown > 0 || isResending) return;

    const result = await resolveMutationResult(resendOtp({ username: emailFromQuery }));

    if (!result.ok) {
      notify.error(result.message);
      return;
    }

    setValue('otp', '');
    notify.success(result.message ?? 'OTP sent successfully.');
    setCooldown(RESEND_COOLDOWN_SEC);
  };

  if (!emailFromQuery) return null;

  return (
    <Form layout="vertical" onFinish={handleSubmit(onSubmit)} className={styles.form}>
      <Typography.Text className={styles.otpHint}>
        We sent a 6-digit code to <strong>{emailFromQuery}</strong>
      </Typography.Text>

      <Form.Item className={styles.otpField}>
        <Controller
          name="otp"
          control={control}
          rules={{
            required: 'Please enter the OTP',
            minLength: { value: 6, message: 'OTP must be 6 digits' },
            maxLength: { value: 6, message: 'OTP must be 6 digits' },
            pattern: { value: /^\d{6}$/, message: 'OTP must be 6 digits' },
          }}
          render={({ field, fieldState }) => (
            <>
              <Input.OTP
                length={6}
                size="large"
                value={field.value}
                onChange={field.onChange}
                disabled={isVerifying}
              />
              {fieldState.error ? (
                <Typography.Text type="danger" className={styles.otpError}>
                  {fieldState.error.message}
                </Typography.Text>
              ) : null}
            </>
          )}
        />
      </Form.Item>

      <Form.Item>
        <AuthSubmitButton label="Verify OTP" loading={isVerifying} />
      </Form.Item>

      <div className={styles.otpActions}>
        <button
          type="button"
          className={styles.resendBtn}
          onClick={handleResend}
          disabled={cooldown > 0 || isResending}
        >
          {cooldown > 0 ? `Resend OTP in ${formatCooldown(cooldown)}` : 'Resend OTP'}
        </button>
        <Link to={ROUTES.FORGOT_PASSWORD} className={styles.forgotLink}>
          Change email
        </Link>
      </div>

      <AuthFooterLink to={ROUTES.LOGIN} label="Back to login" />
    </Form>
  );
}
